import React, { useEffect, useRef, useState, useMemo } from 'react';
import * as d3 from 'd3';
import { get } from '@/features/shared/lib/api';

/**
 * MajorPartnersTreemap
 * - Treemap chart for "Major Partner Research Institutions".
 * - Props:
 *   - items: Array<{ label: string, value: number }>
 *   - scholarId: scholar ID for API call
 * - Renders a responsive treemap with rounded tiles, soft blue palette,
 *   and white labels/values as in the provided screenshot.
 */
export default function MajorPartnersTreemap({ items: externalItems = null, scholarId = null }) {
  // API에서 데이터 가져오기 (externalItems가 없을 때만)
  const [partnersData, setPartnersData] = useState(null);
  const [partnersLoading, setPartnersLoading] = useState(false);
  const [partnersErr, setPartnersErr] = useState(null);

  useEffect(() => {
    // externalItems가 제공되면 API 호출하지 않음
    if (externalItems !== null) {
      return;
    }

    // scholarId가 없으면 API 호출하지 않음
    if (!scholarId) {
      return;
    }

    let alive = true;
    async function fetchPartners() {
      setPartnersLoading(true);
      setPartnersErr(null);
      try {
        const response = await get(`/rm/v1/researcher/${encodeURIComponent(scholarId)}/major_partners`);
        if (alive) {
          if (response?.status && response?.result) {
            setPartnersData(response.result);
          } else {
            setPartnersErr(response?.message || '데이터를 가져오는데 실패했습니다.');
            setPartnersData(null);
          }
        }
      } catch (e) {
        if (alive) {
          setPartnersErr(e?.response?.data?.message || e.message);
        }
      } finally {
        if (alive) {
          setPartnersLoading(false);
        }
      }
    }
    fetchPartners();
    return () => { alive = false; };
  }, [externalItems, scholarId]);

  // 데이터 처리: externalItems 또는 API 데이터 사용
  const items = useMemo(() => {
    if (externalItems !== null && Array.isArray(externalItems)) {
      return externalItems;
    }
    if (partnersData && Array.isArray(partnersData)) {
      return partnersData;
    }
    return null;
  }, [externalItems, partnersData]);
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.innerHTML = '';

    // 로딩 중이면 그리지 않음
    if (externalItems === null && partnersLoading) {
      return;
    }

    // Fallback sample data (스크린샷용)
    const data = Array.isArray(items) && items.length ? items : [];

    const width = el.clientWidth || 900;
    const height = 400; // 스샷 비슷한 비율

    const svg = d3
      .select(el)
      .append('svg')
      .attr('width', '100%')
      .attr('height', height)
      .attr('viewBox', `0 0 ${width} ${height}`);

    // hierarchy 구성
    const root = d3
      .hierarchy({
        children: data.map(d => ({ name: d.label, value: Number(d.value) || 0 })),
      })
      .sum(d => d.value)
      .sort((a, b) => (b.value || 0) - (a.value || 0));

    const treemap = d3
      .treemap()
      .size([width, height])
      .paddingInner(10) // 타일 사이 간격
      .paddingOuter(0)
      .round(true);

    treemap(root);

    // 👉 스샷과 같은 5단계 파란 팔레트 (큰 값일수록 진하게)
    const palette = ['#6880E2', '#7BA3ED', '#96B6EF', '#AFCFF5', '#D9E2F7'];

    const leavesSorted = root.leaves().slice().sort((a, b) => (b.value || 0) - (a.value || 0));
    const colorIndexScale = d3
      .scaleQuantize()
      .domain([0, leavesSorted.length - 1])
      .range(d3.range(palette.length));

    const colorByName = new Map();
    leavesSorted.forEach((d, i) => {
      const idx = colorIndexScale(i);
      colorByName.set(String(d.data.name), palette[idx]);
    });

    const g = svg.append('g');

    // Tooltip 생성 (기존 tooltip이 있으면 제거 후 생성)
    d3.selectAll('.major-partners-tooltip').remove();
    const tooltip = d3.select('body')
      .append('div')
      .attr('class', 'major-partners-tooltip')
      .style('position', 'absolute')
      .style('background', 'rgba(0, 0, 0, 0.85)')
      .style('color', 'white')
      .style('padding', '8px 12px')
      .style('border-radius', '6px')
      .style('font-size', '13px')
      .style('font-weight', '600')
      .style('pointer-events', 'none')
      .style('opacity', 0)
      .style('z-index', 10000)
      .style('box-shadow', '0 4px 12px rgba(0, 0, 0, 0.3)');

    const tiles = g
      .selectAll('g.tile')
      .data(root.leaves())
      .enter()
      .append('g')
      .attr('class', 'tile')
      .attr('transform', d => `translate(${d.x0},${d.y0})`);

    // 사각형(둥근 모서리, 테두리 없이)
    tiles
      .append('rect')
      .attr('rx', 14)
      .attr('ry', 14)
      .attr('width', d => Math.max(0, d.x1 - d.x0))
      .attr('height', d => Math.max(0, d.y1 - d.y0))
      .attr('fill', d => colorByName.get(String(d.data.name)) || palette[palette.length - 1])
      .attr('opacity', 1)
      .style('cursor', 'pointer')
      .on('mouseenter', function(event, d) {
        const name = String(d.data.name || '').replace(/\n/g, ' ');
        const v = Number(d.data.value) || 0;
        tooltip
          .html(`<div>${name}</div><div style="margin-top:4px;font-size:11px;opacity:0.9;">${v}</div>`)
          .style('opacity', 1);
      })
      .on('mousemove', function(event) {
        tooltip
          .style('left', (event.pageX + 10) + 'px')
          .style('top', (event.pageY - 10) + 'px');
      })
      .on('mouseleave', function() {
        tooltip.style('opacity', 0);
      });

    // 라벨 + 값 (좌상단 정렬, 타일 크기에 따라 동적 조정)
    const foreignObjects = tiles
      .append('foreignObject')
      .attr('x', d => {
        const w = d.x1 - d.x0;
        // 작은 타일일수록 여백을 줄임 (최소 8px)
        return Math.max(8, Math.min(24, w * 0.08));
      })
      .attr('y', d => {
        const h = d.y1 - d.y0;
        // 작은 타일일수록 여백을 줄임 (최소 8px)
        return Math.max(8, Math.min(24, h * 0.08));
      })
      .attr('width', d => {
        const w = d.x1 - d.x0;
        const x = Math.max(8, Math.min(24, w * 0.08));
        return Math.max(0, w - x * 2);
      })
      .attr('height', d => {
        const h = d.y1 - d.y0;
        const y = Math.max(8, Math.min(24, h * 0.08));
        return Math.max(0, h - y * 2);
      })
      .style('pointer-events', 'none') // foreignObject는 pointer-events를 none으로 설정하여 rect의 이벤트가 작동하도록
      .append('xhtml:div')
      .style('width', '100%')
      .style('height', '100%')
      .style('color', 'white')
      .style('font-weight', '600')
      .style('line-height', '1.3')
      .style('overflow', 'hidden')
      .style('text-overflow', 'ellipsis')
      .html(d => {
        const name = String(d.data.name || '').replace(/\n/g, '<br/>');
        const v = Number(d.data.value) || 0;
        const w = d.x1 - d.x0;
        const h = d.y1 - d.y0;
        const area = w * h;
        
        // 타일 크기에 따라 폰트 크기 동적 조정
        let nameFontSize = 13;
        let valueFontSize = 11; // 숫자는 더 작게
        let marginTop = 6;
        
        // 작은 타일일 경우 폰트 크기와 여백 줄임
        if (area < 3000) {
          nameFontSize = 10;
          valueFontSize = 9;
          marginTop = 4;
        } else if (area < 6000) {
          nameFontSize = 11;
          valueFontSize = 10;
          marginTop = 5;
        } else if (area < 12000) {
          nameFontSize = 12;
          valueFontSize = 10;
          marginTop = 5;
        }
        
        // 너무 작은 타일은 숫자만 표시
        if (area < 1500) {
          return `<div style="font-size:${valueFontSize}px;text-align:center;display:flex;align-items:center;justify-content:center;height:100%;">${v}</div>`;
        }
        
        // 작은 타일은 텍스트를 축약
        let displayName = name;
        if (area < 4000 && name.length > 15) {
          displayName = name.substring(0, 12) + '...';
        }
        
        return `
          <div style="font-size:${nameFontSize}px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${displayName}</div>
          <div style="margin-top:${marginTop}px;font-size:${valueFontSize}px;">${v}</div>
        `;
      });

    // cleanup: 컴포넌트 언마운트 시 tooltip 제거
    return () => {
      d3.selectAll('.major-partners-tooltip').remove();
    };
  }, [items, externalItems, partnersLoading]);

  // 로딩 상태 처리
  if (externalItems === null && partnersLoading) {
    return (
      <div className="flex items-center justify-center min-h-[260px] text-[#8D9AAF]">
        Loading...
      </div>
    );
  }

  // 에러 상태 처리
  if (externalItems === null && partnersErr) {
    return (
      <div className="flex items-center justify-center min-h-[260px] text-[#F3652D] text-sm">
        {partnersErr}
      </div>
    );
  }

  // 데이터 없음
  if (!items || !items.length) {
    return (
      <div className="flex items-center justify-center min-h-[280px] w-full">
        <div className="flex items-center justify-center w-full py-12 px-6 bg-white border border-[#D6DCE6] rounded-xl text-[#8D9AAF] text-sm">
          데이터가 없습니다.
        </div>
      </div>
    );
  }

  // 카드 배경까지 스샷처럼 쓰고 싶으면 bg 색만 조절해서 쓰면 됨
  return <div ref={ref} className="w-full" />;
}
